import { createSlice } from '@reduxjs/toolkit'
import { getFilters } from 'src/redux/async/bidAnalysis/bid-analysis'

const initialState = {
  filters: {
    toThreshold: null,
    fromThreshold: null,
    sourceLocation: [],
    destinationLocation: [],
    negativeSavingToggle: false,
    alternateSavingToggle: false,
    businessUnit: [],
    category: [],
    baseCurrency: 'USD',
    targetCurrency: localStorage?.getItem('default_currency') || 'USD',
  },
  isFob: true,
  range: {},
  destinations: [],
  sourceLocations: [],
  categories: [],
  businessUnits: [],
  countriesOfOrigin: [],
  analysisName: '',
  toggle_status: false,
  partCategory: '',
  typeOfClickPartAnalysis: '',
  activePartAnalysis: '',
  part: '',
  countries: [],
  selectedAwardType: {},
  awardScenarioData: null,
  refreshAwardScenarioList: false,
  rank: [],
  awardScenarioLoader: true,
  awardScenarioTableLoader: true,
  awardScenarioCount: 8,
  currencySymbol: localStorage?.getItem('default_currency') === 'EUR' ? '€' : '$',
  navigatePath: '',
  chartConfig: {
    chartType: null,
    xAxis: null,
    yAxis: null,
    series: null,
    legend: null,
    values: null,
    scenario: [],
    supplier: [],
    destination: [],
    category: [],
  },
  supplierDataChart: null,
  categoryDataChart: null,
  defaultOwnerFilter: null,
  partAnalysisColumnState: null,
  awardScenarioColumnState: [],
  createScenarioLandingColumnState: [],
  newLaneCount: 0,
}

export const bidAnalysisFiltersSlice = createSlice({
  name: 'bid-analysis-filters',
  initialState,
  reducers: {
    setBidAnalysisFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
      const chartConfigKeys = [
        'chartType',
        'xAxis',
        'yAxis',
        'series',
        'legend',
        'values',
        'scenario',
        'supplier',
        'destination',
        'category',
      ]

      chartConfigKeys.forEach((key) => {
        if (action.payload[key] !== undefined) {
          state.chartConfig[key] = action.payload[key]
        }
      })
    },
    setDestinations: (state, action) => {
      state.destinations = action.payload
    },
    setSourceLocations: (state, action) => {
      state.sourceLocations = action.payload
    },
    setCategories: (state, action) => {
      state.categories = action.payload
    },
    setCountries: (state, action) => {
      state.countries = action.payload
    },
    setBusinessUnits: (state, action) => {
      state.businessUnits = action.payload
    },
    setCountriesOfOrigin: (state, action) => {
      state.countriesOfOrigin = action.payload
    },
    resetBidAnalysisFilters: (state) => {
      state.filters = {
        ...initialState.filters,
        isFob: state.isFob,
        fromThreshold: state.range.min,
        toThreshold: state.range.max,
      }
    },
    setAnalysisName: (state, action) => {
      state.analysisName = action.payload
    },
    setToggleStatus: (state, action) => {
      state.toggle_status = action.payload
    },
    setPartCategory: (state, action) => {
      state.partCategory = action.payload
    },
    setTypeOfClickPartAnalysis: (state, action) => {
      state.typeOfClickPartAnalysis = action.payload
    },
    setRange: (state, action) => {
      state.filters.fromThreshold = action.payload.min
      state.filters.toThreshold = action.payload.max
      state.range = action.payload
    },
    setActivePartAnalysis: (state, action) => {
      state.activePartAnalysis = action.payload
    },
    setBidAnalysisFob: (state, action) => {
      state.isFob = action.payload
    },
    setPart: (state, action) => {
      state.part = action.payload
    },
    setSelectedAwardType: (state, action) => {
      state.selectedAwardType = action.payload
    },
    setAwardScenarioData: (state, action) => {
      console.log('action: ', action)
      state.awardScenarioData = action.payload
    },
    setAwardScenarioLoader: (state, action) => {
      state.awardScenarioLoader = action.payload
    },
    setRefreshAwardScenarioList: (state, action) => {
      state.refreshAwardScenarioList = action.payload
    },
    setAwardScenarioTableLoader: (state, action) => {
      state.awardScenarioTableLoader = action.payload
    },
    setAwardScenarioCount: (state, action) => {
      state.awardScenarioCount = action.payload
    },
    setRank: (state, action) => {
      state.rank = action.payload
    },
    setCurrencySymbol: (state, action) => {
      state.currencySymbol = action.payload
    },
    setNavigatePath: (state, action) => {
      state.navigatePath = action.payload
    },
    resetAllBidFilter: () => initialState,
    updateChartConfig: (state, action) => {
      const { key, value } = action.payload
      if (key in state.chartConfig) {
        state.chartConfig[key] = value
      }
    },
    resetChartConfig: (state) => {
      state.chartConfig = initialState.chartConfig
    },
    setChartConfig: (state, action) => {
      state.chartConfig = {
        ...state.chartConfig,
        ...action.payload,
      }
    },
    setSupplierDataChart: (state, action) => {
      state.supplierDataChart = action.payload
    },
    setCategoryDataChart: (state, action) => {
      state.categoryDataChart = action.payload
    },
    setDefaultOwnerFilter: (state, action) => {
      state.defaultOwnerFilter = action.payload
    },
    setPartAnalysisColumnState: (state, action) => {
      if (!action.payload || !Array.isArray(action.payload)) return

      if (!state.partAnalysisColumnState || !Array.isArray(state.partAnalysisColumnState)) {
        state.partAnalysisColumnState = action.payload
        return
      }

      const existingStateMap = new Map(state.partAnalysisColumnState.map((s) => [s.colId, s]))
      state.partAnalysisColumnState = action.payload.map((newState) => ({
        ...(existingStateMap.get(newState.colId) || {}),
        ...newState,
      }))
    },
    setAwardScenarioColumnState: (state, action) => {
      state.awardScenarioColumnState = action.payload
    },
    setCreateScenarioLandingColumnState: (state, action) => {
      state.createScenarioLandingColumnState = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFilters.fulfilled, (state, action) => {
      state.newLaneCount = action.payload?.data?.new_lane_count ?? 0
    })
  },
})

export const {
  setBidAnalysisFilters,
  resetBidAnalysisFilters,
  setBusinessUnits,
  setCategories,
  setCountriesOfOrigin,
  setDestinations,
  setSourceLocations,
  setAnalysisName,
  setToggleStatus,
  setPartCategory,
  setTypeOfClickPartAnalysis,
  setRange,
  setActivePartAnalysis,
  setBidAnalysisFob,
  setRank,
  resetAllBidFilter,
  setPart,
  setCountries,
  setSelectedAwardType,
  setAwardScenarioData,
  setAwardScenarioLoader,
  setAwardScenarioTableLoader,
  setAwardScenarioCount,
  setCurrencySymbol,
  setRefreshAwardScenarioList,
  setNavigatePath,
  updateChartConfig,
  resetChartConfig,
  setChartConfig,
  setSupplierDataChart,
  setCategoryDataChart,
  setDefaultOwnerFilter,
  setPartAnalysisColumnState,
  setAwardScenarioColumnState,
  setCreateScenarioLandingColumnState,
} = bidAnalysisFiltersSlice.actions

export default bidAnalysisFiltersSlice.reducer
